import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { memo, useMemo } from 'react';
import styled from 'styled-components';

interface Props {
	className?: string;
	title?: string;
	content: string | React.ReactNode;
	variant?: 'info' | 'warning' | 'success' | 'error';
	pageName?: string;
	messageId?: string;
}

const NotificationBox = ({ className, title, variant, content }: Props) => {
	const icon = useMemo(() => {
		switch (variant) {
			case 'error':
				return 'c-remove';
			case 'success':
				return 'c-check';
			case 'warning':
				return 'exclamation-circle';
			default:
				return 'i-circle';
		}
	}, [variant]);

	return (
		<div className={className}>
			<Icon variant={icon} />
			<TextWrapper>
				{title ? (
					<Text variant="p-lg" bold>
						{title}
					</Text>
				) : null}

				{typeof content === 'string' ? <Text variant="p-md">{content}</Text> : content}
			</TextWrapper>
		</div>
	);
};

const TextWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
`;

const Memoized = memo(NotificationBox);
const Styled = styled(Memoized)`
	display: flex;
	align-items: center;
	justify-content: flex-start;

	${({ theme, variant }) => {
		switch (variant) {
			case 'error':
				return `
                --border: ${theme.v2.color.error[800]};
                --color: ${theme.v2.color.error[200]};
                `;
			case 'success':
				return `
                --border:  ${theme.v2.color.success[800]};
                --color: ${theme.v2.color.success[200]};
                `;
			case 'warning':
				return `
                --border:  ${theme.v2.color.warning[800]};
                --color: ${theme.v2.color.warning[200]};
                `;
			default:
				return `
                --border:  ${theme.v2.color.neutral[800]};
                --color: ${theme.v2.color.neutral[200]};
                `;
		}
	}}

	background-color: var(--color);
	border-radius: 8px;
	border: 2px solid var(--border);
	padding: 1em 1.5em 1em 1em;

	& > ${Icon} {
		--size: 1.4rem;
		margin-right: 0.75em;
	}
`;

export { Styled as NotificationBox };
